import { useContext, useEffect, useRef, useState, type ReactElement } from "react"
import { useNavigate } from "react-router-dom";
import { fetchNextOrder, fetchTags, releaseOrder } from '../../services/shipStationAPI';
import { printQrLabel } from '../../services/printQrLabelAPI';
import { useAuth } from '../../contexts/AuthContext';
import { OrderContext } from '../../contexts/OrderContext';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import { getTagNameFromEmail, tagDictionaryMaker } from '../../contexts/TagStorage';
import { getApiErrorMessage } from '../../services/errorUtils';
import { ORDER_RELEASE_REASONS } from '@/shared/types/fulfillmentEvents.types';
import { buildPickNextOrderNavState } from './orderdetail/orderDetailNavState';
import { dropOrderDetailPrefetch, prefetchOrderDetail } from './orderdetail/orderDetailPrefetch';
import { QR_NOT_DETECTED_MESSAGE } from '../scanner/qrScanMessages';
import { qrConfirmsOrder } from './qrConfirmsOrder';
import { OrderLabelScanSheet } from './OrderLabelScanSheet';
import './NextOrderPicker.css';

interface ClaimedPick {
  readonly orderId: string | number;
  readonly orderNumber: string;
  readonly customerName: string;
}

function pickLabelName(order: {
  readonly shipTo?: { readonly name?: string };
  readonly customerName?: string;
}): string {
  return order.shipTo?.name?.trim() || order.customerName?.trim() || '';
}

const NextOrderPicker = (): ReactElement | null => {
  const { user } = useAuth();
  const { settings } = useUserSettings();
  const navigate = useNavigate();
  const orderCtx = useContext(OrderContext);
  const setSelectedOrderId = orderCtx?.setSelectedOrderId;
  const setOrderItems = orderCtx?.setOrderItems;
  const setShipToName = orderCtx?.setShipToName;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<ClaimedPick | null>(null);
  const [printError, setPrintError] = useState('');
  const [scanError, setScanError] = useState('');
  const [printing, setPrinting] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const userTagRef = useRef<string | number | null>(null);
  const printLabelRef = useRef(settings.printScanOrderLabel);
  printLabelRef.current = settings.printScanOrderLabel;

  const openClaimedOrder = (pick: ClaimedPick): void => {
    setSelectedOrderId?.(pick.orderId);
    setOrderItems?.([]);
    setShipToName?.('');
    navigate(`/order/${pick.orderId}`, {
      replace: true,
      state: buildPickNextOrderNavState(),
    });
  };

  const sendLabel = async (pick: ClaimedPick): Promise<void> => {
    setPrinting(true);
    setPrintError('');
    try {
      await printQrLabel(pick.orderNumber, pick.customerName);
    } catch (err: unknown) {
      console.error('Error printing order label:', err);
      setPrintError(getApiErrorMessage(err, 'Failed to print label'));
    } finally {
      setPrinting(false);
    }
  };

  const dismissClaim = async (): Promise<void> => {
    if (!claimed || releasing) return;
    const userTag = userTagRef.current;
    if (userTag == null) {
      setScanError('Could not release this order.');
      return;
    }
    setReleasing(true);
    setScanError('');
    try {
      await releaseOrder(claimed.orderId, userTag, ORDER_RELEASE_REASONS.OTHER);
      dropOrderDetailPrefetch(claimed.orderId);
      setSelectedOrderId?.(null);
      setOrderItems?.([]);
      setShipToName?.('');
      navigate('/order-manager', { replace: true });
    } catch (err: unknown) {
      console.error('Error releasing order:', err);
      setScanError(getApiErrorMessage(err, 'Failed to release order'));
      setReleasing(false);
    }
  };

  useEffect(() => {
    const pickNextOrder = async () => {
      try {
        setLoading(true);

        const tagsResponse = await fetchTags({ forceRefresh: true });
        const tagDictionary = tagDictionaryMaker(tagsResponse);

        const tagName = getTagNameFromEmail(user?.email ?? '', tagDictionary);
        const userTag = tagName ? tagDictionary[tagName] : undefined;

        if (!userTag) {
          setError(`No tag found for user: ${tagName}`);
          return;
        }

        userTagRef.current = userTag;

        const data = await fetchNextOrder(userTag);

        if (data.success && data.order) {
          const orderNumber = String(data.order.orderNumber ?? '').trim();
          const customerName = pickLabelName(data.order);
          setSelectedOrderId?.(data.order.orderId);
          setOrderItems?.([]);
          setShipToName?.('');

          if (!printLabelRef.current) {
            navigate(`/order/${data.order.orderId}`, {
              replace: true,
              state: buildPickNextOrderNavState(),
            });
            return;
          }

          if (!orderNumber || !customerName) {
            setError('Picked order is missing an order number or customer name.');
            return;
          }

          const pick: ClaimedPick = {
            orderId: data.order.orderId,
            orderNumber,
            customerName,
          };
          prefetchOrderDetail(pick.orderId);
          setClaimed(pick);
          await sendLabel(pick);
        } else {
          setError('No orders available');
        }
      } catch (err: unknown) {
        console.error('Error picking order:', err);
        setError(getApiErrorMessage(err, 'Failed to pick order'));
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      void pickNextOrder();
    }
  }, [user?.email, navigate, setSelectedOrderId, setOrderItems, setShipToName]);

  const handleQrCapture = (qrData: string | null): boolean => {
    if (!claimed || releasing) return false;
    if (qrConfirmsOrder(qrData, claimed.orderNumber)) {
      setScanError('');
      return true;
    }
    setScanError(qrData ? 'That QR does not match this order.' : QR_NOT_DETECTED_MESSAGE);
    return false;
  };

  if (loading && !claimed) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div>Picking next order...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div>Error: {error}</div>
      </div>
    );
  }

  if (!claimed) {
    return null;
  }

  return (
    <OrderLabelScanSheet
      title={`Scan order ${claimed.orderNumber} label`}
      heading={(
        <>
          Scan order{' '}
          <span className="order-label-order-number">{claimed.orderNumber}</span>
          {' '}label
        </>
      )}
      closeLabel="Back to order management"
      error={scanError || printError}
      submitting={releasing}
      busyLabel="Releasing order"
      onCapture={handleQrCapture}
      onClose={() => { void dismissClaim(); }}
      onSuccessNavigate={() => { openClaimedOrder(claimed); }}
      extraAction={(
        <button
          type="button"
          className="order-label-reprint"
          onClick={() => { void sendLabel(claimed); }}
          disabled={printing || releasing}
        >
          <span className="order-label-reprint-label">Reprint Label</span>
        </button>
      )}
    />
  );
};

export default NextOrderPicker;
